import {
  AvailableSubscriptionPackagesQuery,
  AvailableSubscriptionPackagesQueryVariables,
} from "@outschool/gql-frontend-generated";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useMemo } from "react";

import { useCreditOffer } from "../../../hooks/useCreditOffer";

const SUBSCRIPTION_PACKAGES_QUERY = gql`
  query AvailableSubscriptionPackages {
    availableSubscriptionPackages {
      uid
      amountCents
      numCredits
      creditValue
      savingsPercentage
      savingsCents
      classPerWeekAvg
      numFreeCredits
    }
  }
`;

// SHIM: Hide legacy tiers from the UI, we sometimes need to unarchive them
// so we can re-enroll legacy users in CX side.
// Long term we should decouple subscription_package.archived_at from display logic.
const LEGACY_TIER_CREDITS = [145, 268, 548, 880, 1440];

export function useSubscriptionPlans() {
  const { data, loading, error } = useQueryWithPreviousData<
    AvailableSubscriptionPackagesQuery,
    AvailableSubscriptionPackagesQueryVariables
  >(SUBSCRIPTION_PACKAGES_QUERY);

  const { creditOffer } = useCreditOffer();

  const subscriptionPlans = useMemo(() => {
    return (data?.availableSubscriptionPackages ?? []).filter(
      plan => !LEGACY_TIER_CREDITS.includes(plan.numCredits),
    );
  }, [data?.availableSubscriptionPackages]);

  // Calculate the trial plan (lowest cost plan available)
  const trialPlan =
    subscriptionPlans.length > 0
      ? subscriptionPlans.reduce((lowest, plan) =>
          plan.amountCents < lowest.amountCents ? plan : lowest,
        )
      : null;

  // Calculate the lowest cost plan eligible for the current credit offer
  // Used for credit offer landing pages
  const creditOfferEligiblePlan = useMemo(() => {
    const eligiblePlanCredits = creditOffer?.eligiblePlans;

    if (!eligiblePlanCredits || eligiblePlanCredits.length === 0) {
      return null;
    }

    const eligiblePlans = subscriptionPlans.filter(plan =>
      eligiblePlanCredits.includes(plan.numCredits),
    );

    return eligiblePlans.length > 0
      ? eligiblePlans.reduce((lowest, plan) =>
          plan.amountCents < lowest.amountCents ? plan : lowest,
        )
      : null;
  }, [creditOffer?.eligiblePlans, subscriptionPlans]);

  return {
    subscriptionPlans,
    trialPlan,
    creditOfferEligiblePlan,
    loading,
    error,
  };
}
